import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useInterventionCreation } from '../../context/interventionCreationContext';
import { apiErrorMessage } from '../Requests/apiError';
import {
  StatusTransition,
  TransitionFailure,
  TransitionResult,
  buildTransitionUrl
} from './assignment';

/** Bilan d'une transition aboutie : chaque demande envoyée tombe dans l'un des deux cas. */
export type TransitionReport = {
  /** Le sens demandé, qui détermine la phrase de succès. */
  transition: StatusTransition;
  submittedCount: number;
  succeededCount: number;
  failedCount: number;
  failures: TransitionFailure[];
};

export type AssignmentTransitionState = {
  runTransition: (transition: StatusTransition) => void;
  isRunning: boolean;
  /** Sens en cours, pour choisir le message du voile de chargement. */
  runningTransition: StatusTransition | null;
  hasFailed: boolean;
  failureReason: string | null;
  report: TransitionReport | null;
  dismiss: () => void;
};

/**
 * Fait changer de statut les demandes cochées, dans un sens ou dans l'autre.
 *
 * `upgrade-status/` et `downgrade-status/` partagent l'URL de base, le corps, la forme de
 * réponse et le traitement du bilan : un seul hook, paramétré par le sens, plutôt que
 * deux jumeaux.
 */
export function useAssignmentTransition(): AssignmentTransitionState {
  const { state, dispatch } = useInterventionCreation();
  const queryClient = useQueryClient();

  const [report, setReport] = useState<TransitionReport | null>(null);

  const mutation = useMutation<TransitionResult, unknown, StatusTransition>({
    mutationFn: async (transition) => {
      if (state.clientId === null) {
        throw new Error('Aucun client sélectionné');
      }

      const response = await axios.post(buildTransitionUrl(transition, state.clientId), {
        pds_ids: state.selectedRequestIds
      });

      return response.data;
    },
    onSuccess: (result, transition) => {
      queryClient.invalidateQueries(['exocet-pds-to-simple-assign']);
      // Un renvoi fait réapparaître les demandes à l'étape Qualification : sans cette
      // seconde invalidation, sa liste et son compteur resteraient périmés. C'est le
      // seul appel de l'étape dans ce cas.
      if (transition === 'sendBack') {
        queryClient.invalidateQueries(['exocet-pds-to-qualify']);
      }

      setReport({
        transition,
        submittedCount: result.total_submitted,
        succeededCount: result.success_count,
        // `failed_ids` est toujours présent, `errors` non : la v1 plantait ici.
        failedCount: result.failed_ids.length,
        failures: result.errors ?? []
      });

      // Les demandes abouties quittent la liste ; celles en échec restent cochées pour
      // être relancées sans tout re-cocher.
      dispatch({ type: 'setSelectedRequests', ids: result.failed_ids });
    }
  });

  return {
    runTransition: mutation.mutate,
    isRunning: mutation.isLoading,
    runningTransition: mutation.isLoading ? (mutation.variables ?? null) : null,
    hasFailed: mutation.isError,
    failureReason: apiErrorMessage(mutation.error),
    report,
    dismiss: () => {
      setReport(null);
      mutation.reset();
    }
  };
}
